// 2026-06-25: Phase 10 — BOM list + detail (single page with right-pane drill-in)
import { cleanCustomerId } from '../../Services/app/operatorservice';
import React, { useEffect, useState, useCallback } from 'react';
import { listBoms, getBomDetail, createBom } from '../../Services/app/workorderservice';
import Swal from 'sweetalert2';
import '../NcLog/NcLog.css';
import './Bom.css';

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 16).replace('T', ' ');
}

const STATUS_COLOR = {
  ACTIVE: '#16a34a', SUPERSEDED: '#9ca3af', DRAFT: '#f59e0b', OBSOLETE: '#dc2626',
};

export default function BomList() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try { setBoms(await listBoms(customerId) || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadDetail = async (bom) => {
    setSelected(bom);
    setDetail(null);
    try { setDetail(await getBomDetail(customerId, bom.id)); } catch (e) { console.error(e); }
  };

  const filtered = showAll ? boms : boms.filter((b) => b.status === 'ACTIVE');

  return (
    <div className="bom-page pages" style={{ paddingTop: 32 }}>
      <div className="nc-header">
        <div>
          <h5 style={{ margin: 0 }}>Bill of Materials</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Revision-controlled · ECN changes spawn new revisions
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show superseded
          </label>
          <button className="nc-btn-secondary" onClick={fetchAll}>Refresh</button>
          <button className="nc-btn-primary" onClick={() => setShowCreate(true)}>+ New BOM</button>
        </div>
      </div>

      <div className="bom-layout">
        <div className="bom-list">
          {loading ? <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
           filtered.length === 0 ? <div style={{ padding: 30, color: '#9ca3af' }}>No BOMs yet.</div> :
          filtered.map((b) => (
            <div
              key={b.id}
              className={`bom-row ${selected?.id === b.id ? 'bom-row-selected' : ''}`}
              onClick={() => loadDetail(b)}
            >
              <div style={{ flex: 1 }}>
                <div className="bom-row-pn">
                  {b.part_number}
                  <span className="bom-rev-pill">Rev {b.revision}</span>
                </div>
                <div className="bom-row-desc">{b.part_description || <em>(no description)</em>}</div>
              </div>
              <div className="bom-row-meta">
                <span className="bom-status-pill" style={{ background: STATUS_COLOR[b.status] || '#9ca3af' }}>
                  {b.status}
                </span>
                <div className="bom-meta-line">{b.line_count} lines</div>
                {b.created_by_ecn_number && (
                  <div className="bom-meta-line">via {b.created_by_ecn_number}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bom-detail">
          {!selected ?
            <div style={{ padding: 30, color: '#9ca3af', textAlign: 'center' }}>← Select a BOM to view details</div> :
           !detail ?
            <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', letterSpacing: 0.5 }}>PART NUMBER</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>
                {detail.header.part_number} <span className="bom-rev-pill" style={{ verticalAlign: 'middle' }}>Rev {detail.header.revision}</span>
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{detail.header.part_description}</div>
            </div>

            <div className="bom-detail-meta">
              <div><span>Status</span><strong style={{ color: STATUS_COLOR[detail.header.status] }}>{detail.header.status}</strong></div>
              <div><span>UOM</span><strong>{detail.header.uom}</strong></div>
              <div><span>Unit Cost</span><strong>₹{Number(detail.header.unit_cost).toLocaleString('en-IN')}</strong></div>
              <div><span>Effective</span><strong>{fmtTime(detail.header.effective_from)}</strong></div>
              {detail.header.created_by_ecn_number && (
                <div><span>Created by</span><strong>{detail.header.created_by_ecn_number}</strong></div>
              )}
              <div><span>Epicor sync</span>
                <strong style={{ color: detail.header.epicor_synced ? '#16a34a' : '#9ca3af' }}>
                  {detail.header.epicor_synced ? '✓ ' + fmtTime(detail.header.epicor_synced_at) : '—'}
                </strong>
              </div>
            </div>

            <h6 style={{ marginTop: 18, marginBottom: 8 }}>Lines ({detail.lines.length})</h6>
            <table className="bom-table">
              <thead>
                <tr><th>Seq</th><th>Part #</th><th>Description</th><th>Qty</th><th>UOM</th><th>Cost</th></tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.line_seq}</td>
                    <td><strong>{l.item_part_number}</strong></td>
                    <td>{l.item_description || '—'}</td>
                    <td>{Number(l.qty)}</td>
                    <td>{l.uom}</td>
                    <td>₹{Number(l.unit_cost).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h6 style={{ marginTop: 24, marginBottom: 8 }}>Revision History</h6>
            <table className="bom-table">
              <thead><tr><th>Rev</th><th>Status</th><th>Created</th><th>via ECN</th></tr></thead>
              <tbody>
                {detail.history.map((h) => (
                  <tr key={h.id} style={h.id === detail.header.id ? { background: '#fef3c7' } : {}}>
                    <td><strong>{h.revision}</strong></td>
                    <td><span className="bom-status-pill" style={{ background: STATUS_COLOR[h.status] }}>{h.status}</span></td>
                    <td>{fmtTime(h.created_ts)}</td>
                    <td>{h.created_by_ecn_number || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>}
        </div>
      </div>

      {showCreate && (
        <BomCreateDialog
          customerId={customerId}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function BomCreateDialog({ customerId, onClose, onCreated }) {
  const [form, setForm] = useState({ part_number: '', part_description: '', uom: 'EACH', unit_cost: 0 });
  const [lines, setLines] = useState([{ item_part_number: '', item_description: '', qty: 1, uom: 'EACH', unit_cost: 0 }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { item_part_number: '', item_description: '', qty: 1, uom: 'EACH', unit_cost: 0 }]);
  const removeLine = (i) => setLines(lines.filter((_, ix) => ix !== i));
  const updateLine = (i, k, v) => setLines(lines.map((l, ix) => ix === i ? { ...l, [k]: v } : l));

  const submit = async () => {
    if (!form.part_number) { Swal.fire('Missing', 'Part number required', 'warning'); return; }
    const validLines = lines.filter((l) => l.item_part_number);
    if (validLines.length === 0) { Swal.fire('Missing', 'At least one line item required', 'warning'); return; }
    setSaving(true);
    try {
      await createBom({ ...form, customer_id: customerId, lines: validLines });
      Swal.fire({ icon: 'success', title: 'BOM created (rev A)', timer: 1300, showConfirmButton: false });
      onCreated();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal bom-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head">
          <h6>New BOM (Rev A)</h6>
          <button onClick={onClose}>×</button>
        </div>
        <div className="nc-modal-body">
          <div className="nc-grid-2">
            <div><label>Part Number *</label><input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></div>
            <div><label>UOM</label><input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} /></div>
            <div><label>Description</label><input value={form.part_description} onChange={(e) => setForm({ ...form, part_description: e.target.value })} /></div>
            <div><label>Unit Cost (₹)</label><input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: Number(e.target.value) })} /></div>
          </div>

          <h6 style={{ marginTop: 14, marginBottom: 6, fontSize: 13 }}>Lines</h6>
          <table className="bom-edit-table">
            <thead>
              <tr><th>Part #</th><th>Description</th><th>Qty</th><th>UOM</th><th>Cost</th><th></th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><input value={l.item_part_number} onChange={(e) => updateLine(i, 'item_part_number', e.target.value)} /></td>
                  <td><input value={l.item_description} onChange={(e) => updateLine(i, 'item_description', e.target.value)} /></td>
                  <td><input type="number" value={l.qty} onChange={(e) => updateLine(i, 'qty', Number(e.target.value))} style={{ width: 70 }} /></td>
                  <td><input value={l.uom} onChange={(e) => updateLine(i, 'uom', e.target.value)} style={{ width: 70 }} /></td>
                  <td><input type="number" value={l.unit_cost} onChange={(e) => updateLine(i, 'unit_cost', Number(e.target.value))} style={{ width: 90 }} /></td>
                  <td><button type="button" onClick={() => removeLine(i)} className="bom-row-del">×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="nc-btn-secondary" onClick={addLine} style={{ marginTop: 6 }}>+ Add line</button>
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create BOM'}</button>
        </div>
      </div>
    </div>
  );
}
